import { useEffect, useState } from "react";
import DataTable from "../components/DataTable";
import AdminListToolkit from "../components/listing/AdminListToolkit";
import type { Column } from "../components/DataTable";
import type { FormField } from "../components/AdminDataModal";
import AdminDataModal from "../components/AdminDataModal";
import { useMessageModal } from "../contexts/MessageModalContext";
import { useFormat } from "../../context/FormatContext";

interface UserGroup {
  id: number;
  name: string;
  description: string;
  status: string;
  created_at: string;
}

export default function UserGroups() {
  const BASE = import.meta.env.VITE_LYRAZONE_BASE_URL;
  const { showModal } = useMessageModal();
  const { formatDateToDMY } = useFormat();

  const [data, setData] = useState<UserGroup[]>([]);
  const [selectedRow, setSelectedRow] = useState<any>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<"add" | "edit" | "view">("add");
  const [activeRow, setActiveRow] = useState<any>(null);

  /* =========================
     FETCH GROUPS
  ========================= */

  const fetchGroups = async () => {
    const res = await fetch(`${BASE}/admin/users/groups`, {
      credentials: "include",
    });

    const result = await res.json();
    const freshData = result.data || [];

    setData(freshData);

    setSelectedRow((prev: any) =>
      prev ? freshData.find((item: any) => item.id === prev.id) || null : null,
    );

    setActiveRow((prev: any) =>
      prev ? freshData.find((item: any) => item.id === prev.id) || null : null,
    );
  };

  useEffect(() => {
    fetchGroups();
  }, []);

  /* =========================
     COLUMNS
  ========================= */

  const columns: Column[] = [
    { key: "id", label: "ID" },
    { key: "name", label: "Group Name" },
    { key: "description", label: "Description" },

    {
      key: "status",
      label: "Status",
      render: (row: UserGroup) => (
        <span
          className={row.status === "active" ? "badge-success" : "badge-danger"}
        >
          {row.status.charAt(0).toUpperCase() + row.status?.slice(1)}
        </span>
      ),
    },

    {
      key: "created_at",
      label: "Created",
      render: (row: UserGroup) => formatDateToDMY(row.created_at),
    },
  ];

  /* =========================
     FORM FIELDS
  ========================= */

  const groupFields: FormField[] = [
    {
      key: "id",
      label: "ID",
      colSpan: 1,
    },
    {
      key: "name",
      label: "Group Name",
      colSpan: 2,
    },

    {
      key: "description",
      label: "Description",
      colSpan: 2,
    },

    {
      key: "status",
      label: "Status",
      type: "select",
      options: [
        { label: "Active", value: "active" },
        { label: "Inactive", value: "inactive" },
      ],
      colSpan: 2,
    },
  ];

  /* =========================
     HANDLE SUBMIT
  ========================= */

  const handleSubmit = async (formData: any) => {
    try {
      const method = modalMode === "add" ? "POST" : "PUT";

      const url =
        modalMode === "add"
          ? `${BASE}/admin/users/group`
          : `${BASE}/admin/users/group/${activeRow.id}`;

      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(formData),
      });

      const result = await res.json();

      if (result.success || res.ok) {
        fetchGroups();
        setModalOpen(false);
        setActiveRow(null);

        showModal({
          type: "success",
          title: "Success",
          message:
            modalMode === "add"
              ? "User group created successfully."
              : "User group updated successfully.",
        });
      }
    } catch (error) {
      console.error(error);
    }
  };

  /* =========================
     DELETE
  ========================= */

  const handleDelete = (row: any) => {
    showModal({
      type: "confirm",
      title: "Delete User Group",
      message: "Are you sure you want to delete this group?",
      confirmText: "Delete",
      onConfirm: async () => {
        await fetch(`${BASE}/admin/users/group/${row.id}`, {
          method: "DELETE",
          credentials: "include",
        });

        fetchGroups();
      },
    });
  };

  /* =========================
     RENDER
  ========================= */

  return (
    <div>
      <AdminListToolkit
        title="User Groups"
        columns={columns}
        data={data}
        selectedRow={selectedRow}
        onAdd={() => {
          setModalMode("add");
          setActiveRow(null);
          setModalOpen(true);
        }}
        onView={(row) => {
          setModalMode("view");
          setActiveRow(row);
          setModalOpen(true);
        }}
        onEdit={(row) => {
          setModalMode("edit");
          setActiveRow(row);
          setModalOpen(true);
        }}
        onSoftDelete={handleDelete}
        enableExport
        exportFileName="user-groups"
      >
        {(filteredData) => (
          <DataTable
            columns={columns}
            data={filteredData}
            selectedRowId={selectedRow?.id}
            onRowSelect={setSelectedRow}
          />
        )}
      </AdminListToolkit>

      <AdminDataModal
        isOpen={modalOpen}
        mode={modalMode}
        title="User Group"
        fields={groupFields}
        rowData={activeRow}
        onClose={() => setModalOpen(false)}
        onSubmit={handleSubmit}
        size="lg"
      />
    </div>
  );
}
